import { z } from 'zod';
import { EventName } from '../ingest/common.ts';
import { FilterValueType, Operand } from './audience.ts';

/**
 * Action-based delivery (BB-100, CMP-003/005): what a user does that sends the campaign, how long to wait, what
 * cancels a waiting send and when sending is allowed. Shared with Canvas action-based entry and Action Paths
 * (BB-111, BB-117). Property conditions use the operators of segment filters (BB-40/BB-42).
 */
export const PropertyCondition = z.strictObject({
  path: z.string().min(1).max(255),
  dataType: FilterValueType,
  operator: z.string().min(1).max(40),
  value: Operand.optional(),
  value2: z.number().optional(),
});
export type PropertyCondition = z.infer<typeof PropertyCondition>;

const AttributeKey = z.string().min(1).max(255);
const LocalTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:mm');

export const TriggerSpec = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('custom_event'), event: EventName, propertyFilters: z.array(PropertyCondition).max(10).default([]) }),
  z.strictObject({ kind: z.literal('session_start'), appInstanceIds: z.array(z.uuid()).max(20).optional() }),
  /** Fires only on a real change of the value (`$language`, `$country`, … or a custom attribute). */
  z.strictObject({
    kind: z.literal('attribute_changed'),
    attribute: AttributeKey,
    to: z.discriminatedUnion('kind', [z.strictObject({ kind: z.literal('any') }), z.strictObject({ kind: z.literal('equals'), value: z.union([z.string(), z.number(), z.boolean()]) })]).default({ kind: 'any' }),
  }),
  z.strictObject({ kind: z.literal('subscription_changed'), groupId: z.uuid(), to: z.enum(['subscribed', 'unsubscribed']) }),
  z.strictObject({ kind: z.literal('message_interaction'), messageApiId: z.uuid(), interaction: z.enum(['click', 'open', 'impression']) }),
]);
export type TriggerSpec = z.infer<typeof TriggerSpec>;

export const TriggerDelay = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('none') }),
  z.strictObject({ kind: z.literal('after'), seconds: z.number().int().min(60).max(90 * 86_400) }),
  /** The next given weekday (1 = Monday) at a wall time, never the same day. */
  z.strictObject({ kind: z.literal('next_weekday'), weekday: z.number().int().min(1).max(7), time: LocalTime, localTime: z.boolean().default(false) }),
  /** N calendar days later at a wall time. */
  z.strictObject({ kind: z.literal('calendar_days'), days: z.number().int().min(1).max(90), time: LocalTime, localTime: z.boolean().default(false) }),
]);
export type TriggerDelay = z.infer<typeof TriggerDelay>;

/** Hours when sending is allowed; outside them the send waits for the next allowed time or is dropped. */
export const TimeFrame = z.strictObject({ from: LocalTime, to: LocalTime, localTime: z.boolean().default(false), sendAtNextAvailable: z.boolean().default(true) });
export type TimeFrame = z.infer<typeof TimeFrame>;

export const ActionBasedDelivery = z.strictObject({
  /** Any of them sends (OR). */
  triggers: z.array(TriggerSpec).min(1).max(10),
  delay: TriggerDelay.default({ kind: 'none' }),
  /** Events that cancel a send still waiting for its delay. */
  cancelOn: z.array(TriggerSpec).max(10).default([]),
  timeFrame: TimeFrame.optional(),
  /** Check the audience again when the delayed send goes out. */
  reevaluateAudienceAtSend: z.boolean().default(false),
});
export type ActionBasedDelivery = z.infer<typeof ActionBasedDelivery>;
